import * as React from "react";
import type { SidebarItemType } from "./SidebarItem";
import { SidebarItem } from "./SidebarItem";
import {
  Box,
  Drawer,
  List,
  ListItem,
  Toolbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import "./Sidebar.css";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";

import { useKeycloak } from "@react-keycloak/web";
import {
  FEEDBACK_URL,
  SUPPORT_URL_URL,
  FAQ_URL_URL,
  RESEARCH_URL_URL,
} from "../../utils/constants";
import { logoutNotificationToExt } from "../../utils/logoutNotificationToExt";

import {
  QuestionMarkIcon,
  QuestionAnswerIcon,
  CampaignIcon,
  accountIconSvg,
  logoutIconSvg,
  homeIconSvg,
  resourceIconSvg,
  researchIconSvg,
} from "./sidebarIconImports";
import { useAppDispatch, useAdmin } from "../../utils/hooks";
import { logout } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import AdminPanelSettingsSharpIcon from "@mui/icons-material/AdminPanelSettingsSharp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import BusinessIcon from "@mui/icons-material/Business";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import {
  ADMIN_RUBRICS_PAGE_ENDPOINT,
  ADMIN_MODELS_PAGE_ENDPOINT,
  ADMIN_ORGANIZATION_PAGE_ENDPOINT,
  PRODUCTS_PAGE_ENDPOINT,
  REPORT_LIST_PAGE_ENDPOINT,
  SETTINGS_PAGE_ENDPOINT,
  USER_RUBRICS_PAGE_ENDPOINT,
  ADMIN_RESOURCE_PAGE_ENDPOINT,
} from "../../routes/endpointConstants";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

type Props = {
  handleDrawerToggle?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  mobileOpen?: boolean;
  isCollapsed?: boolean;
};

export type SidebarBlock = {
  name?: string;
  itemClassName?: string;
  sidebarItems?: Array<SidebarItemType>;
  iconComponent?: React.ReactNode;
};

export const Sidebar: React.FC<Props> = ({
  handleDrawerToggle,
  mobileOpen,
  isCollapsed = false,
}) => {
  const { keycloak } = useKeycloak();
  const dispatch = useAppDispatch();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();

  const [resourceExpanded, setResourceExpanded] = React.useState(() => {
    const saved = localStorage.getItem("sidebar-resource-expanded");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [adminExpanded, setAdminExpanded] = React.useState(() => {
    const saved = localStorage.getItem("sidebar-admin-expanded");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [resourceAnchorEl, setResourceAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [adminAnchorEl, setAdminAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );

  const handleResourceChange = React.useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setResourceExpanded(isExpanded);
      localStorage.setItem(
        "sidebar-resource-expanded",
        JSON.stringify(isExpanded),
      );
    },
    [],
  );

  const handleAdminChange = React.useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setAdminExpanded(isExpanded);
      localStorage.setItem(
        "sidebar-admin-expanded",
        JSON.stringify(isExpanded),
      );
    },
    [],
  );

  const sidebarBlocks: Array<SidebarBlock> = React.useMemo(
    () => [
      {
        itemClassName: "menu-item",
        sidebarItems: [
          {
            name: "Home",
            url: PRODUCTS_PAGE_ENDPOINT,
            iconComponent: <img src={homeIconSvg} alt={"Home"} />,
          },
        ],
      },
      {
        itemClassName: "menu-item",
        sidebarItems: [
          {
            name: "Reports",
            url: REPORT_LIST_PAGE_ENDPOINT,
            iconComponent: <BusinessCenterIcon sx={{ color: "#413E3F" }} />,
          },
        ],
      },
      {
        itemClassName: "menu-item",
        sidebarItems: [
          {
            name: "My Rubrics",
            url: USER_RUBRICS_PAGE_ENDPOINT,
            iconComponent: <AssignmentIcon sx={{ color: "#413E3F" }} />,
          },
        ],
      },
      {
        name: "Resource",
        itemClassName: "menu-item menu-item--expand",
        iconComponent: <img src={resourceIconSvg} alt={"Resource Icon"} />,
        sidebarItems: [
          {
            name: "FAQs",
            url: FAQ_URL_URL,
            iconComponent: <img src={QuestionMarkIcon} alt={"FAQs Icon"} />,
          },
          {
            name: "Support",
            url: SUPPORT_URL_URL,
            iconComponent: (
              <img src={QuestionAnswerIcon} alt={"Support Icon"} />
            ),
          },
          {
            name: "Research",
            url: RESEARCH_URL_URL,
            iconComponent: <img src={researchIconSvg} alt={"Research Icon"} />,
          },
        ],
      },
      {
        itemClassName: "menu-item",
        sidebarItems: [
          {
            name: "Manage Account",
            url: SETTINGS_PAGE_ENDPOINT,
            iconComponent: <img src={accountIconSvg} alt={"account"} />,
          },
        ],
      },
    ],
    [],
  );

  const bottomBlocks: Array<SidebarBlock> = React.useMemo(
    () => [
      {
        itemClassName: "menu-item",
        sidebarItems: [
          {
            name: "Give Feedback",
            url: FEEDBACK_URL,
            iconComponent: <img src={CampaignIcon} alt={"Support Icon"} />,
          },
        ],
      },
      ...(isAdmin
        ? [
            {
              name: "Administration",
              itemClassName: "menu-item menu-item--expand",
              iconComponent: <AdminPanelSettingsSharpIcon />,
              sidebarItems: [
                {
                  name: "Rubrics",
                  url: ADMIN_RUBRICS_PAGE_ENDPOINT,
                  iconComponent: <AssignmentIcon />,
                },
                {
                  name: "Models",
                  url: ADMIN_MODELS_PAGE_ENDPOINT,
                  iconComponent: <SmartToyIcon />,
                },
                {
                  name: "Organizations",
                  url: ADMIN_ORGANIZATION_PAGE_ENDPOINT,
                  iconComponent: <BusinessIcon />,
                },
                {
                  name: "Resources",
                  url: ADMIN_RESOURCE_PAGE_ENDPOINT,
                  iconComponent: <DocumentScannerIcon />,
                },
              ],
            },
          ]
        : []),
      {
        itemClassName: "menu-item menu-item--bottom",
        sidebarItems: [
          {
            name: "Logout",
            url: keycloak?.createLogoutUrl(),
            iconComponent: <img src={logoutIconSvg} alt={"logout"} />,
            performFunction: () => {
              logout(dispatch, keycloak);
              logoutNotificationToExt();
            },
            sameTabForOutsideUrl: true,
          },
        ],
      },
    ],
    [isAdmin, keycloak, dispatch],
  );

  const drawer = (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Toolbar />
      <Box
        sx={{ overflow: "auto" }}
        style={{
          display: "flex",
          flexDirection: "column",
          flexGrow: "1",
        }}
      >
        {sidebarBlocks.map((sb, index) => (
          <React.Fragment key={index}>
            {sb.name === "Resource" && !isCollapsed ? (
              <Accordion
                sx={{
                  boxShadow: "none",
                  borderRadius: 0,
                  padding: 0,
                  margin: 0,
                  "& .MuiAccordionSummary-root": {
                    minHeight: 48,
                    "& .MuiAccordionSummary-content": {
                      margin: "0",
                      alignItems: "center",
                    },
                  },
                }}
                expanded={resourceExpanded}
                onChange={handleResourceChange}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="resources-content"
                  id="resources-header"
                  sx={{
                    borderBottom: "1px solid #d9d9d9",
                    px: 2,
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      margin: 0,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: 24,
                      height: 24,
                      mr: 2,
                      justifyContent: "center",
                      "& img": {
                        width: 20,
                        height: 20,
                        objectFit: "contain",
                      },
                    }}
                  >
                    {sb.iconComponent}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#413E3F",
                      pl: 1.5,
                    }}
                  >
                    {sb.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  {sb.sidebarItems?.map((si, index) => (
                    <SidebarItem
                      key={index}
                      {...si}
                      isCollapsed={isCollapsed}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ) : sb.name === "Resource" && isCollapsed ? (
              <>
                <Tooltip title={sb.name} placement="right">
                  <IconButton
                    onClick={(e) => setResourceAnchorEl(e.currentTarget)}
                    sx={{
                      width: "100%",
                      height: 48,
                      borderRadius: 0,
                      maxWidth: 60,
                    }}
                  >
                    {sb.iconComponent}
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={resourceAnchorEl}
                  open={Boolean(resourceAnchorEl)}
                  onClose={() => setResourceAnchorEl(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  sx={{
                    "& .MuiPaper-root": {
                      boxShadow: "none",
                      border: "1px solid #d9d9d9",
                      minWidth: 200,
                    },
                  }}
                >
                  {sb.sidebarItems?.map((si, index) => (
                    <MenuItem
                      key={index}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          setResourceAnchorEl(null);
                          si.url && window.open(si.url, "_blank");
                          return;
                        }
                        setResourceAnchorEl(null);
                        si.url && window.open(si.url, "_blank");
                      }}
                      onMouseDown={(e) => {
                        if (e.button === 1) {
                          e.preventDefault();
                          setResourceAnchorEl(null);
                          si.url && window.open(si.url, "_blank");
                        }
                      }}
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "16px",
                        fontWeight: 700,
                        minHeight: 48,
                        px: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: 24,
                          height: 24,
                          mr: 2,
                          justifyContent: "center",
                          "& img": {
                            width: 20,
                            height: 20,
                            objectFit: "contain",
                          },
                        }}
                      >
                        {si.iconComponent}
                      </Box>
                      {si.name}
                      <OpenInNewIcon
                        sx={{ ml: "auto", width: 16, height: 16, opacity: 0.6 }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <List className={sb.itemClassName}>
                {sb.name && !isCollapsed && (
                  <ListItem sx={{ minHeight: 48, px: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: 24,
                        height: 24,
                        mr: 2,
                        justifyContent: "center",
                        "& img": {
                          width: 20,
                          height: 20,
                          objectFit: "contain",
                        },
                        "& svg": {
                          width: 20,
                          height: 20,
                        },
                      }}
                    >
                      {sb.iconComponent}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#413E3F",
                      }}
                    >
                      {sb.name}
                    </Typography>
                  </ListItem>
                )}
                {sb.sidebarItems?.map((si, index) => (
                  <SidebarItem key={index} {...si} isCollapsed={isCollapsed} />
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </Box>
      {/* Bottom sections */}
      <Box>
        {bottomBlocks.map((bb, index) => (
          <React.Fragment key={index}>
            {bb.name === "Administration" && !isCollapsed ? (
              <Accordion
                sx={{
                  boxShadow: "none",
                  borderRadius: 0,
                  padding: 0,
                  margin: 0,
                  "& .MuiAccordionSummary-root": {
                    minHeight: 48,
                    "& .MuiAccordionSummary-content": {
                      margin: "0",
                      alignItems: "center",
                    },
                  },
                }}
                expanded={adminExpanded}
                onChange={handleAdminChange}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="administration-content"
                  id="administration-header"
                  sx={{
                    borderBottom: "1px solid #d9d9d9",
                    px: 2,
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      margin: 0,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: 24,
                      height: 24,
                      mr: 2,
                      justifyContent: "center",
                      "& svg": {
                        width: 20,
                        height: 20,
                      },
                    }}
                  >
                    {bb.iconComponent}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#413E3F",
                      pl: 1.5,
                    }}
                  >
                    {bb.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  {bb.sidebarItems?.map((si, index) => (
                    <SidebarItem
                      key={index}
                      {...si}
                      isCollapsed={isCollapsed}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ) : bb.name === "Administration" && isCollapsed ? (
              <>
                <Tooltip title={bb.name} placement="right">
                  <IconButton
                    onClick={(e) => setAdminAnchorEl(e.currentTarget)}
                    sx={{
                      width: "100%",
                      height: 48,
                      borderRadius: 0,
                      maxWidth: 60,
                    }}
                  >
                    {bb.iconComponent}
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={adminAnchorEl}
                  open={Boolean(adminAnchorEl)}
                  onClose={() => setAdminAnchorEl(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  sx={{
                    "& .MuiPaper-root": {
                      boxShadow: "none",
                      border: "1px solid #d9d9d9",
                      minWidth: 200,
                    },
                  }}
                >
                  {bb.sidebarItems?.map((si, index) => (
                    <MenuItem
                      key={index}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          setAdminAnchorEl(null);
                          si.url &&
                            window.open(
                              `${window.location.origin}${si.url}`,
                              "_blank",
                            );
                          return;
                        }
                        setAdminAnchorEl(null);
                        si.url && navigate(si.url);
                      }}
                      onMouseDown={(e) => {
                        if (e.button === 1) {
                          e.preventDefault();
                          setAdminAnchorEl(null);
                          si.url &&
                            window.open(
                              `${window.location.origin}${si.url}`,
                              "_blank",
                            );
                        }
                      }}
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "16px",
                        fontWeight: 700,
                        minHeight: 48,
                        px: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: 24,
                          height: 24,
                          mr: 2,
                          justifyContent: "center",
                          "& svg": {
                            width: 20,
                            height: 20,
                          },
                        }}
                      >
                        {si.iconComponent}
                      </Box>
                      {si.name}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <List className={bb.itemClassName} sx={{ padding: 0 }}>
                {bb.sidebarItems?.map((si, index) => (
                  <SidebarItem key={index} {...si} isCollapsed={isCollapsed} />
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </Box>
    </div>
  );

  const drawerWidth = isCollapsed ? 60 : 250;
  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        {drawer}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            height: "100vh",
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
};
